import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import {
  buildAdProductRows,
  buildAdvertisedProductRows,
  buildAsinsByAdGroup,
  parseSdProductAdsPayload,
  parseSpProductAdsPayload,
  type AdProductRow,
  type AdvertisedProductRow,
  type ProductAdCatalogRow,
} from '@/utils/workspaceProductMetrics';
import { lookupScopeCampaignIdSet } from '@/utils/workspaceAdProductMetrics';

/** Lean Workspace body — no extended fields; skip ARCHIVED. */
async function fetchSpProductAds(profileId: string): Promise<ProductAdCatalogRow[]> {
  const res = await adsApi.listSpProductAds(profileId, {
    include_extended_data_fields: false,
    states: ['ENABLED', 'PAUSED'],
    max_results: 1000,
  });
  if (!res.success) throw new Error('Failed to load SP product ads');
  return parseSpProductAdsPayload(res.data);
}

async function fetchSdProductAds(profileId: string): Promise<ProductAdCatalogRow[]> {
  const res = await adsApi.getSdProductAds(profileId);
  if (!res.success) throw new Error('Failed to load SD product ads');
  return parseSdProductAdsPayload(res.data);
}

export function useWorkspaceProductAds(
  profileId: string,
  campaignNameById: Map<string, string>,
  scopedCampaignLookupKeys: Set<string> | null | undefined,
  enabled: boolean
) {
  const queries = useQueries({
    queries: [
      {
        queryKey: ['workspace-sp-product-ads', profileId, 'lean'],
        queryFn: () => fetchSpProductAds(profileId),
        enabled: Boolean(profileId) && enabled,
        staleTime: 60_000,
      },
      {
        queryKey: ['workspace-sd-product-ads', profileId],
        queryFn: () => fetchSdProductAds(profileId),
        enabled: Boolean(profileId) && enabled,
        staleTime: 60_000,
      },
    ],
  });

  const [spQuery, sdQuery] = queries;
  const scopedCampaignIds = useMemo(
    () => lookupScopeCampaignIdSet(scopedCampaignLookupKeys),
    [scopedCampaignLookupKeys]
  );

  const catalog = useMemo(() => {
    return [...(spQuery.data ?? []), ...(sdQuery.data ?? [])];
  }, [spQuery.data, sdQuery.data]);

  const advertisedProducts: AdvertisedProductRow[] = useMemo(() => {
    return buildAdvertisedProductRows(catalog, new Map(), scopedCampaignIds);
  }, [catalog, scopedCampaignIds]);

  const adProducts: AdProductRow[] = useMemo(() => {
    const asinsByAdGroup = buildAsinsByAdGroup(catalog);
    return buildAdProductRows(
      catalog,
      new Map(),
      campaignNameById,
      asinsByAdGroup,
      scopedCampaignIds
    );
  }, [catalog, campaignNameById, scopedCampaignIds]);

  const isLoading = Boolean(enabled) && queries.some((q) => q.isLoading);
  const firstError = queries.find((q) => q.error)?.error;

  const refetch = () => {
    for (const q of queries) {
      void q.refetch();
    }
  };

  return {
    advertisedProducts,
    adProducts,
    catalog,
    isLoading,
    error:
      firstError instanceof Error ? firstError : firstError ? new Error(String(firstError)) : null,
    refetch,
  };
}
